import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Mail } from "lucide-react";

export default function CoachClients() {
  const [assigned, setAssigned] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = () => api.get("/coach/clients").then((r) => {
    setAssigned(r.data.assigned || []);
    setUnassigned(r.data.unassigned || []);
  });
  useEffect(() => { load().catch(() => {}); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/coach/clients/invite", form);
      toast.success(data.temp_password ? `Invited. Temp password: ${data.temp_password}` : "Client linked.");
      setForm({ name: "", email: "", password: "" });
      setShowInvite(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setTier = async (id, tier) => {
    try {
      await api.patch(`/coach/clients/${id}/tier`, { tier });
      toast.success(tier === "mtm" ? "Full MTM access granted" : "Switched to membership tier");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const assign = async (id) => {
    try {
      await api.post(`/coach/clients/${id}/assign`);
      toast.success("Client assigned.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10" data-testid="coach-clients">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Roster</div>
          <h1 className="font-serif text-5xl mt-3">Clients</h1>
        </div>
        <button data-testid="invite-client-btn" className="btn-primary" onClick={() => setShowInvite(!showInvite)}>
          <UserPlus size={14} /> Invite client
        </button>
      </header>

      {showInvite && (
        <form onSubmit={invite} className="card-luxe space-y-5 max-w-2xl" data-testid="invite-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-luxe">Full name</label>
              <input className="input-luxe" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
            </div>
            <div>
              <label className="label-luxe">Email</label>
              <input type="email" className="input-luxe" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div className="md:col-span-2">
              <label className="label-luxe">Password (optional)</label>
              <input className="input-luxe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto-generated if blank" data-testid="invite-password" />
            </div>
          </div>
          <button className="btn-primary" data-testid="invite-submit">Send invite</button>
        </form>
      )}

      <section>
        <div className="overline mb-4">My clients ({assigned.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assigned.map((c) => (
            <div key={c.user_id} className="card-luxe" data-testid={`assigned-client-${c.user_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-surface-2 rounded-full flex items-center justify-center text-accent font-serif text-lg">
                  {c.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-secondary-luxe flex items-center gap-1"><Mail size={11} />{c.email}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-luxe">
                <label className="label-luxe">Access tier</label>
                <select className="input-luxe text-xs" value={c.tier || "mtm"}
                  onChange={(e) => setTier(c.user_id, e.target.value)}
                  data-testid={`tier-select-${c.user_id}`}>
                  <option value="mtm">MTM · Full coaching access</option>
                  <option value="membership">FENYX Membership · Self-guided</option>
                </select>
              </div>
            </div>
          ))}
          {assigned.length === 0 && <div className="text-secondary-luxe text-sm">No clients yet. Invite your first.</div>}
        </div>
      </section>

      <section>
        <div className="overline mb-4">Unassigned ({unassigned.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {unassigned.map((c) => (
            <div key={c.user_id} className="card-luxe flex items-center justify-between" data-testid={`unassigned-client-${c.user_id}`}>
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-secondary-luxe">{c.email}</div>
              </div>
              <button className="btn-secondary" onClick={() => assign(c.user_id)} data-testid={`assign-${c.user_id}`}>Assign</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
